/**
 * Connectivity Indicator Component
 * Shows online/offline status to the user
 */

import { isOnline, addConnectivityListeners } from '../utils/serviceWorker';
import { t } from '../i18n/config';

export class ConnectivityIndicator {
  constructor() {
    this.element = null;
    this.isVisible = false;
  }

  /**
   * Initialize the connectivity indicator
   */
  init() {
    this.createElement();
    this.attachToDOM();
    this.setupListeners();

    // Check initial status
    if (!isOnline()) {
      this.show('offline');
    }
  }

  /**
   * Create the indicator element
   */
  createElement() {
    this.element = document.createElement('div');
    this.element.id = 'connectivity-indicator';
    this.element.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      padding: 12px 20px;
      text-align: center;
      font-size: 14px;
      font-weight: 500;
      z-index: 9999;
      transform: translateY(-100%);
      transition: transform 0.3s ease-out;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    `;
  }

  /**
   * Attach indicator to DOM
   */
  attachToDOM() {
    document.body.appendChild(this.element);
  }

  /**
   * Setup connectivity listeners
   */
  setupListeners() {
    addConnectivityListeners(
      () => this.show('online'),
      () => this.show('offline')
    );
  }

  /**
   * Show the indicator
   * @param {string} status - 'online' or 'offline'
   */
  show(status) {
    if (status === 'offline') {
      this.element.style.background = '#FF6B6B';
      this.element.style.color = 'white';
      this.element.innerHTML = t('app.connectivity.offline');
      this.element.style.transform = 'translateY(0)';
      this.isVisible = true;
    } else if (status === 'online') {
      this.element.style.background = '#51CF66';
      this.element.style.color = 'white';
      this.element.innerHTML = t('app.connectivity.online');
      this.element.style.transform = 'translateY(0)';
      this.isVisible = true;

      // Auto-hide after 3 seconds
      setTimeout(() => this.hide(), 3000);
    }
  }

  /**
   * Hide the indicator
   */
  hide() {
    this.element.style.transform = 'translateY(-100%)';
    this.isVisible = false;
  }

  /**
   * Destroy the indicator
   */
  destroy() {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}

// Export singleton instance
export const connectivityIndicator = new ConnectivityIndicator();
